/**
 * Crave Club — the loyalty programme the demo is built around.
 *
 * The commercial argument of the product: members see inventory non-members
 * cannot. Premium unlocks each restaurant's `memberTimes`, which is why a
 * sold-out card turns bookable the moment a tier is connected.
 *
 * This replaces what was a card-issuer tie-in in the earlier prototype. Keeping
 * it as data means the partner can be swapped without touching components.
 */

/** @typedef {import('../lib/types.js').MemberTier} MemberTier */

export const MEMBER_TIERS = {
  premium: {
    id: 'premium',
    label: 'Crave Club Premium',
    shortLabel: 'Premium',
    price: '$18 / month',
    /** Premium is the only tier that reveals hidden inventory. */
    unlocksInsiderTables: true,
    tagline: 'Insider tables · priority waitlist · $100 quarterly dining credit',
    perks: [
      'Insider Tables at restaurants showing as fully booked',
      'Priority position on every waitlist',
      '$100 dining credit each quarter',
      'Complimentary cancellation up to 2 hours before',
    ],
  },
  plus: {
    id: 'plus',
    label: 'Crave Club Plus',
    shortLabel: 'Plus',
    price: '$8 / month',
    unlocksInsiderTables: false,
    tagline: '$50 dining credit twice a year · standard waitlist',
    perks: ['$50 dining credit every six months', 'Standard waitlist alerts', 'No booking fees'],
  },
};

/** Copy for the "Insider Tables" concept, referenced in several sheets. */
export const INSIDER_LABEL = 'Insider Table';

/**
 * Whether a tier can see the hidden `memberTimes` inventory.
 *
 * @param {MemberTier} tier
 * @returns {boolean}
 */
export const unlocksInsiderTables = (tier) =>
  Boolean(tier && MEMBER_TIERS[tier]?.unlocksInsiderTables);

/**
 * The reservation slots a given guest can actually book.
 *
 * This is the single rule that decides whether a card looks available or sold
 * out, so it lives here rather than being re-derived in each component.
 *
 * @param {import('../lib/types.js').Restaurant} restaurant
 * @param {MemberTier} tier
 * @returns {string[]}
 */
export function bookableTimes(restaurant, tier) {
  if (!restaurant.waitlistOnly) return restaurant.times;
  return unlocksInsiderTables(tier) ? restaurant.memberTimes : [];
}

/**
 * True when a restaurant is out of reach for this guest — public inventory is
 * gone and they have no tier that unlocks more.
 *
 * A real booking link counts as reachable even with no times of our own. Venues
 * discovered from Google have no `times` at all, because Google cannot supply
 * reservation inventory and Crave has no partner access to hold a table — so
 * without this they would every one of them read as sold out and offer a
 * waitlist that does not exist. Those hand off to the venue's own booking page
 * instead (see `ExternalReservationHandoff`); the Insider Tables mechanic still
 * governs venues we curate times for.
 *
 * @param {import('../lib/types.js').Restaurant} restaurant
 * @param {MemberTier} tier
 */
export const isSoldOut = (restaurant, tier) =>
  // Only a venue marked waitlist-only is sold out; one with no times and no
  // link is usually a walk-in place. Mirrors mobile/src/data/membership.ts.
  Boolean(restaurant.waitlistOnly) &&
  bookableTimes(restaurant, tier).length === 0 &&
  !restaurant.reservationUrl;
